import axios from 'axios'

const emptyDate = '0001-01-01T00:00:00Z'

function showWhitelistExpirationWarning(commit, context, count, validTo) {
  if (!context) {
    return
  }

  const validToDate = validTo instanceof Date ? validTo : new Date(validTo)
  if (Number.isNaN(validToDate.getTime()) || validToDate.getFullYear() <= 1) {
    return
  }

  const languageCode = context.$vuetify.lang.current
  let locale = 'en-GB'
  if (languageCode === 'et') {
    locale = 'et-EE'
  }
  if (languageCode === 'lv') {
    locale = 'lv-LV'
  }
  const date = validToDate.toLocaleDateString(locale, {
    day: languageCode === 'et' ? '2-digit' : 'numeric',
    month: languageCode === 'et' ? '2-digit' : 'long',
    year: 'numeric'
  })
  const titleKey =
    count === 1
      ? '$vuetify.EDIT_WHITELISTS.EXPIRATION_WARNING_TITLE_ONE'
      : '$vuetify.EDIT_WHITELISTS.EXPIRATION_WARNING_TITLE'
  const title = context.$vuetify.lang.t(titleKey, count)
  const body = context.$vuetify.lang.t(
    '$vuetify.EDIT_WHITELISTS.EXPIRATION_WARNING_BODY',
    date
  )

  commit(
    'app/setSnackbar',
    {
      show: true,
      type: 'warning',
      message: `${title}. ${body}`
    },
    { root: true }
  )
}

async function checkWhitelistExpirationWarning(
  commit,
  dispatch,
  context,
  config,
  whitelistId,
  validTo
) {
  if (!whitelistId || !validTo || validTo === emptyDate) {
    return
  }

  const validToDate = validTo instanceof Date ? validTo : new Date(validTo)
  if (Number.isNaN(validToDate.getTime()) || validToDate.getFullYear() <= 1) {
    return
  }

  try {
    const response = await axios.get(
      `${process.env.VUE_APP_URL}/adminapi/whitelists/${whitelistId}/expiration-warning`,
      {
        ...config,
        params: {
          validTo: validToDate.toISOString()
        }
      }
    )
    const warning = response.data || {}
    if (warning.AffectedUsersCount > 0) {
      showWhitelistExpirationWarning(
        commit,
        context,
        warning.AffectedUsersCount,
        warning.EffectiveValidTo || validTo
      )
    }
  } catch (error) {
    if (error && error.response && error.response.status === 401) {
      dispatch('authentication/logout', null, { root: true })
    }
  }
}

export default {
  getWhitelists({ commit, dispatch, state, rootState }, payload) {
    if (
      !state.whitelists.length ||
      payload.includeAvailabilityInfo ||
      payload.forceRefresh
    ) {
      commit('setWhitelistsLoaded', false)
      return new Promise((resolve, reject) => {
        commit(
          'app/setLoading',
          { type: 'getWhitelists', state: true },
          { root: true }
        )
        commit('app/setError', null, { root: true })

        const config = {
          headers: { Authorization: `Bearer ${rootState.authentication.jwt}` }
        }
        let url = `${process.env.VUE_APP_URL}/adminapi/whitelists-with-availability?availability=${payload.withAvailabilityInfo}${payload.queryString}`
        if (
          rootState.app.vehicleNumberSearchString &&
          rootState.app.vehicleNumberSearchString !== ''
        ) {
          url += `&vehicleNumber=${rootState.app.vehicleNumberSearchString}`
        }
        if (payload.includeAvailabilityInfo) {
          axios
            .get(url, config)
            .then(response => {
              const whitelists = response.data
              commit('setWhitelists', whitelists)
              commit('setWhitelistsLoaded', true)
              resolve()
              commit(
                'app/setLoading',
                { type: '', state: false },
                { root: true }
              )
              commit('app/setError', null, { root: true })
            })
            .catch(error => {
              if (error && error.response.status === 401) {
                dispatch('authentication/logout', null, { root: true })
              }
              commit(
                'app/setLoading',
                { type: '', state: false },
                { root: true }
              )
              commit('app/setError', error, { root: true })
              reject()
              commit('setWhitelistsLoaded', true)
            })
        }
      })
    }
    return false
  },
  /**
   * Gets and sets whitelist by whitelist id
   * @param {Number} id
   */
  getWhitelist: async ({ commit, rootState, dispatch }, id) => {
    commit(
      'app/setLoading',
      { type: 'getWhitelist', state: true },
      { root: true }
    )
    commit('app/setError', null, { root: true })
    try {
      const config = {
        headers: { Authorization: `Bearer ${rootState.authentication.jwt}` }
      }
      await axios
        .get(`${process.env.VUE_APP_URL}/adminapi/whitelists/${id}`, config)
        .then(response => {
          commit('setWhitelist', response.data)
          commit('app/setLoading', { type: '', state: false }, { root: true })
          commit('app/setError', null, { root: true })
        })
        .catch(error => {
          if (error.response.status === 401) {
            dispatch('authentication/logout', null, { root: true })
          }
        })
    } catch (error) {
      if (error && error.response.status === 401) {
        dispatch('authentication/logout', null, { root: true })
      }
      commit(
        'app/setLoading',
        { type: 'getWhitelist', state: false },
        { root: true }
      )
      commit('app/setError', error, { root: true })
    }
  },
  /**
   * Gets and sets whitelis users by whitelist id
   * @param {Number} id - whitelist id
   */
  getWhitelistUsers: async ({ commit, dispatch, rootState }, id) => {
    commit(
      'app/setLoading',
      { type: 'getWhitelistUsers', state: true },
      { root: true }
    )
    commit('app/setError', null, { root: true })
    try {
      const config = {
        headers: { Authorization: `Bearer ${rootState.authentication.jwt}` }
      }
      await axios
        .get(
          `${process.env.VUE_APP_URL}/adminapi/whitelists/${id}/users`,
          config
        )
        .then(response => {
          commit('setWhitelistValue', {
            key: 'Users',
            value: response.data
          })
          commit('app/setLoading', { type: '', state: false }, { root: true })
          commit('app/setError', null, { root: true })
        })
    } catch (error) {
      if (error.response.status === 401) {
        dispatch('authentication/logout', null, { root: true })
      }
      commit(
        'app/setLoading',
        { type: 'getWhitelistUsers', state: false },
        { root: true }
      )
      commit('app/setError', error, { root: true })
    }
  },
  /**
   * Gets and sets whitelist availability info by whitelist id
   * @param {Number} whitelistId
   */
  getAvailabilityInfo: async ({ commit, rootState }, whitelistId) => {
    commit('setAvailabilityInfo', [])
    commit(
      'app/setLoading',
      { type: 'getAvailabilityInfo', state: true },
      { root: true }
    )
    commit('app/setError', null, { root: true })
    try {
      const config = {
        headers: { Authorization: `Bearer ${rootState.authentication.jwt}` }
      }
      await axios
        .get(
          `${process.env.VUE_APP_URL}/adminapi/whitelists/${whitelistId}/current-availability-info`,
          config
        )
        .then(response => {
          commit('setAvailabilityInfo', response.data)
          commit('app/setLoading', { type: '', state: false }, { root: true })
          commit('app/setError', null, { root: true })
        })
    } catch (error) {
      commit(
        'app/setLoading',
        { type: 'getAvailabilityInfo', state: false },
        { root: true }
      )
      commit('app/setError', error, { root: true })
    }
  },
  /**
   * Removes whitelist by id
   * @param {Number} whitelistId
   */
  removeWhitelist: async ({ commit, dispatch, rootState }, whitelist) => {
    commit(
      'app/setLoading',
      { type: 'removeWhitelist', state: true },
      { root: true }
    )
    commit('app/setError', null, { root: true })
    try {
      const config = {
        headers: { Authorization: `Bearer ${rootState.authentication.jwt}` }
      }
      await axios
        .delete(
          `${process.env.VUE_APP_URL}/adminapi/whitelists/${whitelist.ID}`,
          config
        )
        .then(() => {
          commit('removeWhitelistById', whitelist.ID)
          commit('app/setLoading', { type: '', state: false }, { root: true })
          commit('app/setError', null, { root: true })
        })
    } catch (error) {
      if (error && error.response.status === 401) {
        dispatch('authentication/logout', null, { root: true })
      }
      commit(
        'app/setLoading',
        { type: 'removeWhitelist', state: false },
        { root: true }
      )
      commit('app/setError', error, { root: true })
    }
  },
  /**
   * Post whitelist
   * @param {Object} - whitelist object
   */
  postWhitelist: async ({ commit, dispatch, rootState }, payload) => {
    const context = payload.this
    const whitelist = payload.whitelist || payload
    commit(
      'app/setLoading',
      { type: 'postWhitelist', state: true },
      { root: true }
    )
    commit('app/setError', null, { root: true })
    try {
      const config = {
        headers: { Authorization: `Bearer ${rootState.authentication.jwt}` }
      }
      if (Array.isArray(whitelist.Zones)) {
        commit('setWhitelistValue', {
          key: 'Zones',
          value: `|${whitelist.Zones.join('|')}|`
        })
      }
      if (whitelist.ID) {
        let savedWhitelist = null
        await axios
          .put(
            `${process.env.VUE_APP_URL}/adminapi/whitelists/${whitelist.ID}`,
            whitelist,
            config
          )
          .then(response => {
            savedWhitelist = response.data
            commit('setWhitelistInWhitelistsArray', response.data)
            commit(
              'app/setSnackbar',
              {
                show: true,
                type: 'success',
                message: 'Whitelist updated successfully!'
              },
              { root: true }
            )
            commit('app/setLoading', { type: '', state: false }, { root: true })
            commit('app/setError', null, { root: true })
          })
        if (savedWhitelist) {
          await checkWhitelistExpirationWarning(
            commit,
            dispatch,
            context,
            config,
            savedWhitelist.ID,
            savedWhitelist.ValidTo
          )
        }
      } else {
        await axios
          .post(
            `${process.env.VUE_APP_URL}/adminapi/whitelists`,
            whitelist,
            config
          )
          .then(response => {
            commit('addWhitelist', response.data)
            commit(
              'app/setSnackbar',
              {
                show: true,
                type: 'success',
                message: 'New whitelist added successfully!'
              },
              { root: true }
            )
            commit(
              'app/setLoading',
              { type: 'postWhitelist', state: false },
              { root: true }
            )
            commit('app/setError', null, { root: true })
          })
      }
    } catch (error) {
      if (whitelist.Zones && whitelist.Zones.length) {
        commit('setWhitelistValue', {
          key: 'Zones',
          value: whitelist.Zones.slice(1, -1).split('|')
        })
      }
      if (error && error.response.status === 401) {
        dispatch('authentication/logout', null, { root: true })
      }
      commit(
        'app/setSnackbar',
        {
          show: true,
          type: 'error',
          message:
            error.response && error.response.data
              ? error.response.data.Error
              : `Something went wrong! Please try again later.`
        },
        { root: true }
      )
      commit(
        'app/setLoading',
        { type: 'postWhitelist', state: false },
        { root: true }
      )
      commit('app/setError', error, { root: true })
    }
  },
  /**
   * Post whitelist user
   * @param {WhitelistUser} - whitelist user object
   */
  postWhitelistUser: async (
    { commit, state, dispatch, rootState },
    payload
  ) => {
    const context = payload.this
    const user = payload.user || payload
    user.StartSessionsWithLPR = state.whitelist.StartSessionsWithLPR
    user.ChargeForSessions = state.whitelist.ChargeForSessions // By default the value of "chargeForSession" is true and can be modified directly in DB
    commit(
      'app/setLoading',
      { type: 'postWhitelistUser', state: true },
      { root: true }
    )
    commit('app/setError', null, { root: true })
    try {
      const config = {
        headers: { Authorization: `Bearer ${rootState.authentication.jwt}` }
      }
      // if (payload.ClientId.length) {
      //   payload.ClientId = Number(payload.ClientId)
      // }
      if (user.ID) {
        let savedWhitelistUser = null
        await axios
          .put(
            `${process.env.VUE_APP_URL}/adminapi/whitelists/${user.WhitelistId}/users/${user.ID}`,
            user,
            config
          )
          .then(response => {
            const savedUser =
              response.data && response.data.ID ? response.data : user
            savedWhitelistUser = savedUser
            commit(
              'app/setSnackbar',
              {
                show: true,
                type: 'success',
                message: 'User updated successfully!'
              },
              { root: true }
            )
            commit('updateWhitelistUser', savedUser)
            commit('app/setLoading', { type: '', state: false }, { root: true })
            commit('app/setError', null, { root: true })
          })
        if (savedWhitelistUser) {
          await checkWhitelistExpirationWarning(
            commit,
            dispatch,
            context,
            config,
            savedWhitelistUser.WhitelistId ||
              user.WhitelistId ||
              state.currentWhitelistId,
            state.whitelist.ValidTo
          )
        }
      } else {
        let savedWhitelistUser = null
        await axios
          .post(
            `${process.env.VUE_APP_URL}/adminapi/whitelists/${state.currentWhitelistId}/users`,
            user,
            config
          )
          .then(response => {
            savedWhitelistUser = response.data
            commit('addUserToWhitelist', response.data)
            commit(
              'app/setSnackbar',
              {
                show: true,
                type: 'success',
                message: 'User added successfully!'
              },
              { root: true }
            )
            commit(
              'app/setLoading',
              { type: 'postWhitelistUser', state: false },
              { root: true }
            )
            commit('app/setError', null, { root: true })
          })
        if (savedWhitelistUser) {
          await checkWhitelistExpirationWarning(
            commit,
            dispatch,
            context,
            config,
            savedWhitelistUser.WhitelistId || state.currentWhitelistId,
            state.whitelist.ValidTo
          )
        }
      }
    } catch (error) {
      if (error.response.status === 401) {
        dispatch('authentication/logout', null, { root: true })
      }
      commit(
        'app/setSnackbar',
        {
          show: true,
          type: 'error',
          message:
            error.response && error.response.data
              ? error.response.data.Error
              : `Something went wrong! Please try again later.`
        },
        { root: true }
      )
      commit(
        'app/setLoading',
        { type: 'postWhitelistUser', state: false },
        { root: true }
      )
      commit('app/setError', error, { root: true })
    }
  },
  /**
   * Post whitelist user
   * @param {WhitelistUser} - whitelist user object
   */
  postWhitelistUsers: async (
    { commit, dispatch, state, rootState },
    payload
  ) => {
    commit(
      'app/setLoading',
      { type: 'postWhitelistUsers', state: true },
      { root: true }
    )
    commit('app/setError', null, { root: true })
    try {
      const config = {
        headers: { Authorization: `Bearer ${rootState.authentication.jwt}` }
      }
      await axios
        .post(
          `${process.env.VUE_APP_URL}/adminapi/whitelists/${state.currentWhitelistId}/users?many=true`,
          payload,
          config
        )
        .then(response => {
          response.data.forEach(user => {
            commit('addUserToWhitelist', user)
          })
          commit(
            'app/setSnackbar',
            {
              show: true,
              type: 'success',
              message: 'Users added successfully!'
            },
            { root: true }
          )
          commit(
            'app/setLoading',
            { type: 'postWhitelistUsers', state: false },
            { root: true }
          )
          commit('app/setError', null, { root: true })
        })
    } catch (error) {
      if (error.response.status === 401) {
        dispatch('authentication/logout', null, { root: true })
      }
      commit(
        'app/setSnackbar',
        {
          show: true,
          type: 'error',
          message:
            error.response && error.response.data
              ? error.response.data.Error
              : `Something went wrong! Please try again later.`
        },
        { root: true }
      )
      commit(
        'app/setLoading',
        { type: 'postWhitelistUsers', state: false },
        { root: true }
      )
      commit('app/setError', error, { root: true })
    }
  },
  /**
   * Delete whitelist user
   * @param {WhitelistUser} - whitelist user object
   */
  deleteWhitelistUser: async ({ commit, dispatch, rootState }, payload) => {
    commit(
      'app/setLoading',
      { type: 'deleteWhitelistUser', state: true },
      { root: true }
    )
    commit('app/setError', null, { root: true })
    try {
      const config = {
        headers: { Authorization: `Bearer ${rootState.authentication.jwt}` }
      }
      await axios
        .delete(
          `${process.env.VUE_APP_URL}/adminapi/whitelists/${payload.WhitelistId}/users/${payload.ID}`,
          { headers: config.headers, data: payload }
        )
        .then(() => {
          commit('removeWhitelistUser', payload)
          commit(
            'app/setSnackbar',
            {
              show: true,
              type: 'success',
              message: 'User removed successfully'
            },
            { root: true }
          )
          commit('app/setLoading', { type: '', state: false }, { root: true })
          commit('app/setError', null, { root: true })
        })
    } catch (error) {
      if (error.response.status === 401) {
        dispatch('authentication/logout', null, { root: true })
      }
      commit(
        'app/setSnackbar',
        {
          show: true,
          type: 'error',
          message: error.response.data
            ? error.response.data.Error
            : 'Something went wrong! Please try again later.'
        },
        { root: true }
      )
      commit(
        'app/setLoading',
        { type: 'deleteWhitelistUser', state: false },
        { root: true }
      )
      commit('app/setError', error, { root: true })
    }
  },
  /**
   * Generates Excel reports file for whitelist
   */
  getWhitelistXLSX({ commit, rootState }, payload) {
    return new Promise((resolve, reject) => {
      commit(
        'app/setLoading',
        { type: 'getWhitelistXLSX', state: true },
        { root: true }
      )
      commit('app/setError', null, { root: true })
      try {
        const config = {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${rootState.authentication.jwt}`,
            Accept:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }
        }
        axios
          .get(
            `${process.env.VUE_APP_URL}/adminapi/whitelists/${payload.id}/manager-report?page=0&from=${payload.validFrom}&to=${payload.validTo}&type=${payload.type}`,
            config
          )
          .then(response => {
            resolve(response)
            commit('app/setLoading', { type: '', state: false }, { root: true })
            commit('app/setError', null, { root: true })
          })
      } catch (error) {
        reject(error)
        commit(
          'app/setLoading',
          { type: 'getWhitelistXLSX', state: false },
          { root: true }
        )
        commit('app/setError', error, { root: true })
      }
    })
  },
  /**
   * Get report rows
   */
  getReportRows({ commit, rootState }, payload) {
    return new Promise((resolve, reject) => {
      commit(
        'app/setLoading',
        { type: 'getReportRows', state: true },
        { root: true }
      )
      commit('app/setError', null, { root: true })
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${rootState.authentication.jwt}`
          }
        }
        axios
          .get(
            `${process.env.VUE_APP_URL}/adminapi/whitelists/${
              payload.id
            }/manager-report?page=${rootState.app.currentPageNumber - 1}&from=${
              payload.from
            }&to=${payload.to}&type=${payload.type}&car=${
              payload.licencePlate
            }`,
            config
          )
          .then(response => {
            const nextPage = parseInt(response.headers['x-next-page'], 10)
            if (nextPage) {
              commit('setNextAvailablePage', nextPage)
            }
            resolve(response.data)
            commit('app/setLoading', { type: '', state: false }, { root: true })
            commit('app/setError', null, { root: true })
          })
      } catch (error) {
        reject(error)
        commit(
          'app/setLoading',
          { type: 'getReportRows', state: false },
          { root: true }
        )
        commit('app/setError', error, { root: true })
      }
    })
  },
  /**
   * Accept or Decline PENDING status whitelist user
   * @param {WhitelistUser} - whitelist user object
   */
  updateAndSaveWhitelistUserStatus: async (
    { commit, state, rootState },
    { whitelistUserId, status }
  ) => {
    commit(
      'app/setLoading',
      { type: 'updateAndSaveWhitelistUserStatus', state: true },
      { root: true }
    )
    commit('app/setError', null, { root: true })
    try {
      const config = {
        headers: { Authorization: `Bearer ${rootState.authentication.jwt}` }
      }
      await axios
        .put(
          `${process.env.VUE_APP_URL}/adminapi/whitelists/${state.currentWhitelistId}/users/${whitelistUserId}/status/${status}`,
          null,
          config
        )
        .then(response => {
          commit('removeWhitelistUser', response.data)
          commit('addUserToWhitelist', response.data)
          commit(
            'app/setSnackbar',
            {
              show: true,
              type: 'success',
              message: 'Status updated successfully'
            },
            { root: true }
          )
          commit(
            'app/setLoading',
            { type: 'updateAndSaveWhitelistUserStatus', state: false },
            { root: true }
          )
          commit('app/setError', null, { root: true })
        })
    } catch (error) {
      commit(
        'app/setSnackbar',
        {
          show: true,
          type: 'error',
          message:
            error.response && error.response.data
              ? error.response.data.Error
              : `Something went wrong! Please try again later.`
        },
        { root: true }
      )
      commit(
        'app/setLoading',
        { type: 'updateAndSaveWhitelistUserStatus', state: false },
        { root: true }
      )
      commit('app/setError', error, { root: true })
    }
  },
  addWhitelistToFavorites: async ({ commit, rootState }, payload) => {
    commit(
      'app/setLoading',
      { type: 'addWhitelistToFavorites', state: true },
      { root: true }
    )
    commit('app/setError', null, { root: true })
    try {
      const config = {
        headers: { Authorization: `Bearer ${rootState.authentication.jwt}` }
      }
      await axios
        .post(
          `${process.env.VUE_APP_URL}/adminapi/favorite-whitelists`,
          {
            WhitelistId: payload.whitelist.ID
          },
          config
        )
        .then(() => {
          commit(
            'app/setSnackbar',
            {
              show: true,
              type: 'success',
              message: payload.this.$vuetify.lang.t(
                '$vuetify.EDIT_WHITELISTS.ADDED_TO_FAVORITES'
              )
            },
            { root: true }
          )
          commit('app/setLoading', { type: '', state: false }, { root: true })
          commit('app/setError', null, { root: true })
        })
    } catch {
      commit(
        'app/setSnackbar',
        {
          show: true,
          type: 'error',
          message: payload.this.$vuetify.lang.t('$vuetify.ERROR_GLOBAL')
        },
        { root: true }
      )
    }
  },
  removeWhitelistFromFavorites: async ({ commit, rootState }, payload) => {
    commit(
      'app/setLoading',
      { type: 'addWhitelistToFavorites', state: true },
      { root: true }
    )
    commit('app/setError', null, { root: true })
    try {
      const config = {
        headers: { Authorization: `Bearer ${rootState.authentication.jwt}` }
      }
      await axios
        .delete(
          `${process.env.VUE_APP_URL}/adminapi/favorite-whitelists/${payload.whitelist.ID}`,
          { headers: config.headers }
        )
        .then(() => {
          commit(
            'app/setSnackbar',
            {
              show: true,
              type: 'success',
              message: payload.this.$vuetify.lang.t(
                '$vuetify.EDIT_WHITELISTS.REMOVED_FROM_FAVORITES'
              )
            },
            { root: true }
          )
          commit('app/setLoading', { type: '', state: false }, { root: true })
          commit('app/setError', null, { root: true })
        })
    } catch {
      commit(
        'app/setSnackbar',
        {
          show: true,
          type: 'error',
          message: payload.this.$vuetify.lang.t('$vuetify.ERROR_GLOBAL')
        },
        { root: true }
      )
    }
  },
  getWhitelistXLSXReport({ commit, rootState }) {
    return new Promise((resolve, reject) => {
      commit(
        'app/setLoading',
        { type: 'getWhitelistXLSXReport', state: true },
        { root: true }
      )
      commit('app/setError', null, { root: true })
      try {
        const config = {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${rootState.authentication.jwt}`,
            Accept:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }
        }
        axios.get(`${process.env.VUE_APP_URL}/adminapi/whitelists/report`, config)
          .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'report.xlsx')
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
            resolve(response)
            commit('app/setLoading', { type: '', state: false }, { root: true })
            commit('app/setError', null, { root: true })
          })
          .catch(error => {
            reject(error)
            commit('app/setLoading', { type: '', state: false }, { root: true })
            commit('app/setError', error, { root: true })
          })
      } catch (error) {
        reject(error)
        commit(
          'app/setLoading',
          { type: 'getWhitelistXLSXReport', state: false },
          { root: true }
        )
        commit('app/setError', error, { root: true })
      }
    })
  }
}
